<template>
  <div
    ref="container"
    style="height: calc(100% - 64px); display: flex; flex-direction: column; z-index: 0"
  >
    <v-container v-if="loading" style="height: 100%" class="d-flex justify-center align-center">
      <v-progress-circular color="primary" indeterminate size="64" />
    </v-container>
    <h1 v-else-if="error" style="margin: 30px">
      Napaka pri nalaganju PDF datoteke <br />
      {{ error }} <br />
      Prosimo poskusite znova ali kontaktirajte administratorja.
    </h1>
    <konva-stage v-else ref="stage" :config="configKonva" class="konva_stage">
      <konva-layer draggable="true">
        <konva-image
          :config="{
            image: image,
          }"
          @wheel="zoomImage"
        />
        <slot name="overlay" />
      </konva-layer>
    </konva-stage>
  </div>
</template>

<script>
import Pdf from '@/helpers/pdf';

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Preview',
  props: {
    page: {
      type: Number,
      default: 1,
    },
    url: {
      type: [String, Uint8Array],
      default: null,
    },
    rotate: {
      type: Number,
      default: 0,
    },
    refreshCanvasSize: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      configKonva: {
        width: 0,
        height: 0,
      },
      error: null,
      image: null,
      renderer: null,
      loading: false,
      container: HTMLElement,
    };
  },
  watch: {
    page(value) {
      this.setPage(value);
    },
    url(value) {
      this.image = null;
      this.error = null;
      if (value) {
        this.renderPdf(value);
      }
    },
    rotate() {
      this.rotateImageRight();
    },
    refreshCanvasSize() {
      this.resizeCanvas();
    },
  },
  async mounted() {
    if (this.url) {
      await this.renderPdf(this.url);
    }
  },
  methods: {
    zoomImage(scrollEvent) {
      scrollEvent.evt.preventDefault();
      if (!this.$refs.stage) return;
      const konvaStage = this.$refs.stage.getStage();

      const oldScale = konvaStage.scaleX();

      // compute the distance to the zoom point before applying zoom
      const pointerPosition = konvaStage.getPointerPosition();

      if (!pointerPosition) {
        return;
      }

      const mousePointTo = {
        x: (pointerPosition.x - konvaStage.x()) / oldScale,
        y: (pointerPosition.y - konvaStage.y()) / oldScale,
      };

      // compute new scale
      const scaleFactor = oldScale * 0.1;
      const scrollDirection = scrollEvent.evt.deltaY > 0 ? -1 : 1;
      const newScale = oldScale + scaleFactor * scrollDirection;

      konvaStage.scale({ x: newScale, y: newScale });

      // Important - move the stage so that the zoomed point remains
      // visually in place
      const newPos = {
        x: pointerPosition.x - mousePointTo.x * newScale,
        y: pointerPosition.y - mousePointTo.y * newScale,
      };

      konvaStage.position(newPos);
      konvaStage.batchDraw();
    },
    loadImage(srcImage) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          this.image = image;
          this.configKonva = {
            width: image.width,
            height: image.height,
          };
          this.loading = false;
          resolve(image.height);
        };
        image.onerror = reject;
        image.src = srcImage;
      });
    },
    resizeCanvas() {
      if (!this.$refs.stage || !this.image) return;
      const konvaStage = this.$refs.stage.getStage();
      const container = this.$refs.container;
      const containerWidth = container.offsetWidth || 0;
      const containerHeight = container.offsetHeight || 0;

      const heightRatio = containerHeight / this.configKonva.height;
      const widthRatio = containerWidth / this.configKonva.width;

      const imageAspectRatio = this.image.width / this.image.height;
      const containerAspectRatio = containerWidth / containerHeight;
      const containerScale = containerAspectRatio > imageAspectRatio ? heightRatio : widthRatio;

      konvaStage.width(containerWidth);
      konvaStage.height(containerHeight);
      konvaStage.scale({
        x: containerScale,
        y: containerScale,
      });

      // Offset stage so rotation is middle centered
      const xOffset = this.image.width / 2;
      const yOffset = this.image.height / 2;

      // When screen is very wide, center the pdf in the middle
      const middleCenterOffset = containerWidth / 2 - (this.image.width * containerScale) / 2;

      konvaStage.offsetX(xOffset);
      konvaStage.offsetY(yOffset);

      konvaStage.position({
        x: xOffset * containerScale + middleCenterOffset,
        y: yOffset * containerScale,
      });
      konvaStage.batchDraw();
    },
    async renderPdf(url) {
      try {
        this.loading = true;
        this.renderer = await new Pdf(url);
        if (!this.renderer.pdf) {
          this.image = null;
          return;
        }
        const { dataUrl } = await this.renderer.getPage(1);
        await this.loadImage(dataUrl);
        this.resizeCanvas();
        this.$emit('num-pages', this.renderer.pdf.numPages);
        this.$emit('image-data', this.configKonva);
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
    async setPage(page) {
      try {
        const { dataUrl } = await this.renderer.getPage(page);
        await this.loadImage(dataUrl);
      } catch (error) {
        this.error = error;
      }
    },
    rotateImageRight() {
      if (!this.$refs.stage) return;
      const konvaStage = this.$refs.stage.getStage();
      konvaStage.rotate(90);
      konvaStage.batchDraw();
    },
  },
};
</script>

<style scoped>
.konva_stage {
  height: 100%;
  display: flex;
  width: 100%;
}
</style>
